import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  HStack,
  Spacer,
  Progress,
  Spinner,
  Portal,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import CourseDrawer from "./CourseDrawer";
import { DataTable } from "@root/components/datatable";
import { ColumnDef } from "@tanstack/react-table";
import { useSubscriberContext } from "@root/context/use-subscriber-context";
import { useSelector } from "react-redux";
import { getLoggedInUser } from "@root/store/selectors/entities";
import { USER_TYPES } from "@root/constants";
import { formatProgress } from "@root/utils";
import SearchInput from "@root/components/common/SearchInput";
import { usePledgeManageCoursesTourGuide } from "@root/hooks/usePledgeManageCoursesTourGuide";
import { usePledgeCourseDrawerTourGuide } from "@root/hooks/usePledgeCourseDrawerTourGuide";
import { TourGuide, RestartTourButton } from "@root/components/tour-guide";

const ManageCourses = () => {
  const { t } = useTranslation("common");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const { pledgeManageCoursesTourGuide, isMobile: isMobileManageCourses } =
    usePledgeManageCoursesTourGuide();
  const { pledgeCourseDrawerTourGuide, isMobile: isMobileDrawer } =
    usePledgeCourseDrawerTourGuide({
      isOpen: drawerOpen,
      hasCourse: !!selectedCourse,
    });
  const {
    fetchSubscriberCourses,
    subscriberCourses,
    pageNo,
    setPageNo,
    pageSize,
    pageCount,
    fetchSubscriberJournalists,
    subscriberJournalist,
    fetchSubscriberCoursesLoading,
  } = useSubscriberContext();
  const user = useSelector(getLoggedInUser);
  console.log(user); //debug log

  const payload = {
    offset: (pageNo - 1) * pageSize,
    limit: pageSize,
    ...(searchTerm && { search: searchTerm }),
  };

  const handlePageChange = (newPage: number) => {
    setPageNo(newPage);

    const payload = {
      offset: (newPage - 1) * pageSize,
      limit: pageSize,
      ...(searchTerm && { search: searchTerm }),
    };

    if (
      user?.role == USER_TYPES.SUBSCRIBER ||
      user?.role == USER_TYPES.NON_SUBSCRIBER
    ) {
      fetchSubscriberCourses(Number(user?.id), payload);
    } else if (user?.role == USER_TYPES.SUBSUBSCRIBER) {
      fetchSubscriberCourses(Number(user?.subscriberId), payload);
    }
  };
  useEffect(() => {
    if (
      user?.role == USER_TYPES.SUBSCRIBER ||
      user?.role == USER_TYPES.NON_SUBSCRIBER
    ) {
      fetchSubscriberCourses(Number(user?.id), payload);
    } else if (user?.role == USER_TYPES.SUBSUBSCRIBER) {
      fetchSubscriberCourses(Number(user?.subscriberId), payload);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      const searchPayload = {
        offset: 0,
        limit: pageSize,
        ...(searchTerm && { search: searchTerm }),
      };
      setPageNo(1); // Reset to first page when searching
      if (
        user?.role == USER_TYPES.SUBSCRIBER ||
        user?.role == USER_TYPES.NON_SUBSCRIBER
      ) {
        fetchSubscriberCourses(Number(user?.id), searchPayload);
      } else if (user?.role == USER_TYPES.SUBSUBSCRIBER) {
        fetchSubscriberCourses(Number(user?.subscriberId), searchPayload);
      }
    }
  }, [searchTerm]);

  const handleRowClick = async (course: any) => {
    const response = await fetchSubscriberJournalists(
      course?.course?.id,
      payload,
    );
    console.log("course?.course?.id,", course);
    if (!response) return;
    setSelectedCourse({
      courseId: course?.course?.id,
      maintitle: course?.course?.title,
      overallProgress: course?.overallProgress,
      ...response,
    });
    setDrawerOpen(true);
  };

  const columns = useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        header: t("manageCourses.courseTitle"),
        accessorKey: "title",
        cell: ({ row }) => (
          <Text fontWeight={500}>{row?.original?.course?.title}</Text>
        ),
      },
      {
        header: t("manageCourses.memberEnrolled"),
        accessorKey: "enrolled",
        cell: ({ row }) => row?.original?.registeredUser,
      },
      {
        header: t("topCourses.category"),
        accessorKey: "status",
        cell: ({ row }) => {
          const category = row?.original?.course?.category?.title || "-";
          const subcategory = row?.original?.course?.subcategory?.title || "";

          return (
            <Flex direction="column" align="start" width="fit-content">
              <Text>{category}</Text>
              {subcategory && <Text fontSize="sm">→ {subcategory}</Text>}
            </Flex>
          );
        },
      },
      {
        header: t("manageCourses.completionRate"),
        accessorKey: "completion",
        cell: ({ row }) =>
          row.original.completion !== null ? (
            <HStack>
              <Progress
                value={row?.original?.overallProgress}
                size="sm"
                colorScheme={
                  row?.original?.completion === 100 ? "green" : "blue"
                }
                borderRadius="8px"
                w="100px"
                bg="#E4EBF0"
              />
              <Text fontWeight={500}>
                {formatProgress(row?.original?.overallProgress)}%
              </Text>
            </HStack>
          ) : (
            <Text color="gray.400">{t("manageCourses.null")}</Text>
          ),
      },
      {
        header: t("manageCourses.lastUpdated"),
        accessorKey: "updated",
        cell: ({ row }) => {
          const rawDate = row?.original?.course?.updatedAt;
          if (!rawDate) return "-";

          const date = new Date(rawDate);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0"); // Month is 0-indexed
          const day = String(date.getDate()).padStart(2, "0");

          return `${year}-${month}-${day}`;
        },
      },
    ],
    [t],
  );

  return (
    <>
      <Box minH="100vh" width={"100%"} py={4} px={8}>
        {/* Header */}
        <Text fontWeight={600} fontSize="24px" mb={1}>
          {t("manageCourses.title")}
        </Text>
        <Text color="gray.500" mb={6}>
          {t("manageCourses.subtitle")}
        </Text>

        {/* Actions */}
        <Flex
          mb={4}
          gap={2}
          flexWrap={{ base: "wrap", md: "nowrap" }}
          align="center"
          width="100%"
        >
          <HStack flex={1}>
            <SearchInput
              data-tour-item="pledge-manage-courses-search"
              placeholder={t("manageCourses.searchPlaceholder")}
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchTerm(e.target.value)
              }
              width="500px"
            />
          </HStack>

          <Spacer />
        </Flex>

        {/* DataTable */}
        <Box
          p={0}
          overflowX="auto"
          minW="320px"
          data-tour-item="pledge-manage-courses-table"
        >
          {fetchSubscriberCoursesLoading ? (
            <Flex
              flexDir={"column"}
              justifyContent={"center"}
              alignItems={"center"}
              height={"350px"}
              gap={3}
            >
              <Spinner size="lg" color="#1819B4" />
            </Flex>
          ) : (
            <DataTable
              data={subscriberCourses}
              columns={columns}
              showPagination={true}
              page={pageNo}
              pageCount={pageCount}
              pageSize={pageSize}
              handlePageChange={handlePageChange}
              onRowSelectionChangeHandler={undefined}
              canEditRow={false}
              getRowId={(row: any) => row.title}
              renderSubComponent={undefined}
              getRowCanExpand={undefined}
              getRowSelected={undefined}
              onRowSelectionChange={undefined}
              onRowClick={handleRowClick}
            />
          )}
        </Box>
        <CourseDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          course={selectedCourse}
        />
      </Box>

      {/* Manage Courses Tour */}
      {!isMobileManageCourses &&
        !drawerOpen &&
        !pledgeManageCoursesTourGuide.isActive && (
          <Portal>
            <RestartTourButton
              onClick={pledgeManageCoursesTourGuide.startTour}
              position={{ bottom: "24px", right: "24px" }}
            />
          </Portal>
        )}
      {!isMobileManageCourses &&
        !drawerOpen &&
        pledgeManageCoursesTourGuide.isActive &&
        pledgeManageCoursesTourGuide.currentStepData && (
          <TourGuide
            isActive={pledgeManageCoursesTourGuide.isActive}
            currentStep={pledgeManageCoursesTourGuide.currentStepData}
            currentStepIndex={pledgeManageCoursesTourGuide.currentStep}
            totalSteps={pledgeManageCoursesTourGuide.totalSteps}
            onNext={pledgeManageCoursesTourGuide.nextStep}
            onBack={pledgeManageCoursesTourGuide.previousStep}
            onClose={pledgeManageCoursesTourGuide.stopTour}
            targetSelector={
              pledgeManageCoursesTourGuide.currentStepData.targetSelector
            }
            borderRadius="8px"
          />
        )}

      {/* Course Drawer Tour */}
      {!isMobileDrawer &&
        drawerOpen &&
        !pledgeCourseDrawerTourGuide.isActive && (
          <Portal>
            <RestartTourButton
              onClick={pledgeCourseDrawerTourGuide.startTour}
              position={{ bottom: "24px", right: "24px" }}
              zIndex={1500}
            />
          </Portal>
        )}
      {!isMobileDrawer &&
        drawerOpen &&
        pledgeCourseDrawerTourGuide.isActive &&
        pledgeCourseDrawerTourGuide.currentStepData && (
          <TourGuide
            isActive={pledgeCourseDrawerTourGuide.isActive}
            currentStep={pledgeCourseDrawerTourGuide.currentStepData}
            currentStepIndex={pledgeCourseDrawerTourGuide.currentStep}
            totalSteps={pledgeCourseDrawerTourGuide.totalSteps}
            onNext={pledgeCourseDrawerTourGuide.nextStep}
            onBack={pledgeCourseDrawerTourGuide.previousStep}
            onClose={pledgeCourseDrawerTourGuide.stopTour}
            targetSelector={
              pledgeCourseDrawerTourGuide.currentStepData.targetSelector
            }
            borderRadius="8px"
          />
        )}
    </>
  );
};

export default ManageCourses;
